import React, { Component } from 'react';
import { connect } from 'react-redux';
import { Link } from 'react-router-dom';
import {
  Tabs,
  Form,
  Input,
  Button,
  Alert,
  message,
  Select,
  Checkbox,
} from 'antd';
import {
  openAuthView,
  closeAuthView,
  clearSession,
  setSession,
  loginUser,
  registerUser,
  verifyUserRegistration,
  resendUserRegistrationVerification,
  requestPasswordReset,
  resetPassword,
} from '../../modules/auth';
import COUNTRIES from '../../constants/countries';
import './authPage.css';

// Development preview shortcut:
// const DEV_PREVIEW_EMAIL = 'shutter@example.com';
// const DEV_PREVIEW_PASSWORD = 'Password123!';

function getErrorMessage(error, fallback) {
  if (
    error
    && error.response
    && error.response.data
    && error.response.data.message
  ) {
    return error.response.data.message;
  }
  return fallback;
}

class AuthPage extends Component {
  constructor(props) {
    super(props);

    const { auth } = props;

    this.state = {
      activeTab: auth.authViewMode || 'login',
      loggingIn: false,
      registering: false,
      resendingVerification: false,
      requestingResetOtp: false,
      resettingPassword: false,
      loginNeedsOtp: false,
      pendingVerificationEmail: '',
      loginStatusMessage: null,
      disableLoginSubmit: true,
      disableRegisterSubmit: true,
      disableResetSubmit: true,
      resetStep: 'request',
      resetEmail: '',
      resetStatusMessage: null,
    };

    this.loginFormRef = React.createRef();
    this.registerFormRef = React.createRef();
    this.resetFormRef = React.createRef();
  }

  componentDidMount() {
    this.updateLoginButtonState();
    this.updateRegisterButtonState();
    this.updateResetButtonState();
  }

  componentDidUpdate(prevProps, prevState) {
    const { auth } = this.props;
    const { pendingVerificationEmail } = this.state;

    if (auth.authViewMode !== prevProps.auth.authViewMode) {
      this.setState({
        activeTab: auth.authViewMode || 'login',
      });
    }

    if (prevProps.auth.authViewVisible && !auth.authViewVisible) {
      this.resetForms();
    }

    if (
      pendingVerificationEmail
      && pendingVerificationEmail !== prevState.pendingVerificationEmail
      && this.loginFormRef.current
    ) {
      this.loginFormRef.current.setFieldsValue({ email: pendingVerificationEmail });
      this.updateLoginButtonState();
    }

    if (
      this.state.resetEmail
      && this.state.resetEmail !== prevState.resetEmail
      && this.resetFormRef.current
    ) {
      this.resetFormRef.current.setFieldsValue({ email: this.state.resetEmail });
      this.updateResetButtonState();
    }

    if (prevState.loginNeedsOtp !== this.state.loginNeedsOtp) {
      this.updateLoginButtonState();
    }

    if (
      prevState.resetStep !== this.state.resetStep
      || prevState.resetEmail !== this.state.resetEmail
      || prevState.resetStatusMessage !== this.state.resetStatusMessage
    ) {
      this.updateResetButtonState();
    }
  }

  setActiveTab = (key) => {
    this.setState({
      activeTab: key,
    });
    const { auth } = this.props;
    if (auth.authViewMode !== key) {
      auth.openAuthView(key);
    }
  };

  resetForms() {
    if (this.loginFormRef.current) {
      this.loginFormRef.current.resetFields();
    }
    if (this.registerFormRef.current) {
      this.registerFormRef.current.resetFields();
    }
    if (this.resetFormRef.current) {
      this.resetFormRef.current.resetFields();
    }

    this.setState({
      loggingIn: false,
      registering: false,
      resendingVerification: false,
      requestingResetOtp: false,
      resettingPassword: false,
      loginNeedsOtp: false,
      pendingVerificationEmail: '',
      loginStatusMessage: null,
      activeTab: 'login',
      disableLoginSubmit: true,
      disableRegisterSubmit: true,
      disableResetSubmit: true,
      resetStep: 'request',
      resetEmail: '',
      resetStatusMessage: null,
    });
  }

  updateLoginButtonState = () => {
    const form = this.loginFormRef.current;
    if (!form) {
      return;
    }

    const requiredFields = ['email', 'password'];
    if (this.state.loginNeedsOtp) {
      requiredFields.push('otp');
    }

    const hasErrors = form.getFieldsError().some(({ errors }) => errors.length);
    const values = form.getFieldsValue(requiredFields);
    const hasEmptyRequired = requiredFields.some((field) => {
      const value = values[field];
      return value === undefined || value === null || value === '';
    });

    const shouldDisable = hasErrors || hasEmptyRequired;
    if (this.state.disableLoginSubmit !== shouldDisable) {
      this.setState({ disableLoginSubmit: shouldDisable });
    }
  };

  updateRegisterButtonState = () => {
    const form = this.registerFormRef.current;
    if (!form) {
      return;
    }

    const requiredFields = [
      'email',
      'username',
      'company',
      'phone',
      'country',
      'password',
      'confirmPassword',
      'termsAccepted',
    ];

    const hasErrors = form.getFieldsError().some(({ errors }) => errors.length);
    const values = form.getFieldsValue(requiredFields);
    const hasEmptyRequired = requiredFields.some((field) => {
      const value = values[field];
      if (field === 'termsAccepted') {
        return value !== true;
      }
      return value === undefined || value === null || value === '';
    });

    const shouldDisable = hasErrors || hasEmptyRequired;
    if (this.state.disableRegisterSubmit !== shouldDisable) {
      this.setState({ disableRegisterSubmit: shouldDisable });
    }
  };

  updateResetButtonState = () => {
    const form = this.resetFormRef.current;
    if (!form) {
      return;
    }

    const requiredFields = this.state.resetStep === 'confirm'
      ? ['email', 'otp', 'password', 'confirmPassword']
      : ['email'];

    const hasErrors = form.getFieldsError().some(({ errors }) => errors.length);
    const values = form.getFieldsValue(requiredFields);
    const hasEmptyRequired = requiredFields.some((field) => {
      const value = values[field];
      return value === undefined || value === null || value === '';
    });

    const shouldDisable = hasErrors || hasEmptyRequired;
    if (this.state.disableResetSubmit !== shouldDisable) {
      this.setState({ disableResetSubmit: shouldDisable });
    }
  };

  handleLoginFieldsChange = () => {
    this.updateLoginButtonState();
  };

  handleRegisterFieldsChange = () => {
    this.updateRegisterButtonState();
  };

  handleResetFieldsChange = () => {
    this.updateResetButtonState();
  };

  openPasswordReset = () => {
    const loginEmail = this.loginFormRef.current
      ? this.loginFormRef.current.getFieldValue('email')
      : '';
    const resetEmail = (loginEmail || this.state.resetEmail || '').trim().toLowerCase();

    this.setState({
      activeTab: 'reset',
      resetStep: 'request',
      resetEmail,
      resetStatusMessage: 'Enter your email address and we will send a one-time password to reset your account.',
    });
    if (this.resetFormRef.current) {
      this.resetFormRef.current.setFieldsValue({
        email: resetEmail || undefined,
      });
      this.updateResetButtonState();
    }

    const { auth } = this.props;
    if (auth.authViewMode !== 'reset') {
      auth.openAuthView('reset');
    }
  };

  handleResetRequest = async (values) => {
    this.setState({ requestingResetOtp: true });
    const normalizedEmail = (values.email || '').trim().toLowerCase();

    try {
      const responseData = await this.props.auth.requestPasswordReset({
        email: normalizedEmail,
      });
      message.success((responseData && responseData.message) || 'If the email is registered, a reset code has been sent.');
      this.setState({
        resetStep: 'confirm',
        resetEmail: normalizedEmail,
        resetStatusMessage: 'Check your email for the one-time password, then enter it below with your new password.',
      });
      if (this.resetFormRef.current) {
        this.resetFormRef.current.setFieldsValue({
          email: normalizedEmail,
          otp: undefined,
          password: undefined,
          confirmPassword: undefined,
        });
        this.updateResetButtonState();
      }
    } catch (error) {
      const errorMessage = getErrorMessage(error, 'Failed to send reset code.');
      message.error(errorMessage);
    } finally {
      this.setState({ requestingResetOtp: false });
    }
  };

  handleResetConfirm = async (values) => {
    this.setState({ resettingPassword: true });
    const normalizedEmail = (values.email || '').trim().toLowerCase();

    try {
      const responseData = await this.props.auth.resetPassword({
        email: normalizedEmail,
        otp: (values.otp || '').trim(),
        password: values.password,
      });
      message.success((responseData && responseData.message) || 'Password updated successfully.');

      if (this.loginFormRef.current) {
        this.loginFormRef.current.setFieldsValue({
          email: normalizedEmail,
        });
        this.updateLoginButtonState();
      }

      if (this.resetFormRef.current) {
        this.resetFormRef.current.resetFields();
      }

      this.setState({
        activeTab: 'login',
        resetStep: 'request',
        resetEmail: normalizedEmail,
        resetStatusMessage: null,
        loginNeedsOtp: false,
        pendingVerificationEmail: '',
        loginStatusMessage: 'Your password has been reset. Log in with the new password.',
      });
      this.props.auth.openAuthView('login');
    } catch (error) {
      const errorMessage = getErrorMessage(error, 'Failed to reset password.');
      message.error(errorMessage);
    } finally {
      this.setState({ resettingPassword: false });
    }
  };

  handleResetSubmit = async (values) => {
    if (this.state.resetStep === 'confirm') {
      return this.handleResetConfirm(values);
    }
    return this.handleResetRequest(values);
  };

  handleResetBack = () => {
    const resetEmail = this.state.resetEmail;
    this.setState({
      resetStep: 'request',
      resetStatusMessage: 'Enter your email address and we will send a one-time password to reset your account.',
      disableResetSubmit: false,
    });
    if (this.resetFormRef.current) {
      this.resetFormRef.current.setFieldsValue({
        email: resetEmail || undefined,
        otp: undefined,
        password: undefined,
        confirmPassword: undefined,
      });
      this.updateResetButtonState();
    }
  };

  handleLogin = async (values) => {
    this.setState({ loggingIn: true });
    const { auth } = this.props;
    const normalizedEmail = (values.email || '').trim().toLowerCase();

    if (values.otp && values.otp.trim()) {
      try {
        const verifyData = await auth.verifyUserRegistration({
          email: normalizedEmail,
          otp: values.otp.trim(),
        });
        if (verifyData) {
          message.success('Email verified. You are now signed in.');
          this.setState({
            loginNeedsOtp: false,
            loginStatusMessage: null,
          });
          this.setState({ loggingIn: false });
          return;
        }
      } catch (verificationError) {
        const verifyMessage = getErrorMessage(verificationError, 'Failed to verify code.');
        message.error(verifyMessage);
        this.setState({ loggingIn: false });
        return;
      }
    }

    try {
      const responseData = await auth.loginUser({
        email: normalizedEmail,
        password: values.password,
      });
      if (responseData && responseData.user) {
        message.success('Logged in successfully.');
      } else {
        message.success('Login successful.');
        auth.closeAuthView();
      }
    } catch (error) {
      const errorMessage = getErrorMessage(error, 'Failed to login.');
      if (errorMessage && errorMessage.toLowerCase().includes('email verification required')) {
        message.warning('Check your email for the 6 digit code to activate your account.');
        this.setState({
          loginNeedsOtp: true,
          pendingVerificationEmail: normalizedEmail,
          loginStatusMessage: 'Enter the 6 digit code we emailed you to finish verifying your account.',
        });
      } else {
        message.error(errorMessage);
      }
      // Preview-only fallback (uncomment when API is unavailable):
      // if (normalizedEmail === DEV_PREVIEW_EMAIL && values.password === DEV_PREVIEW_PASSWORD) {
      //   auth.setSession({
      //     id: 'dev-preview-user',
      //     email: DEV_PREVIEW_EMAIL,
      //     username: 'shutter',
      //     name: 'Shutter Preview User',
      //     roles: ['user'],
      //   }, 'dev-preview-token');
      //   message.success('Logged in with preview credentials.');
      // }
    } finally {
      this.setState({ loggingIn: false });
    }
  };

  handleRegister = async (values) => {
    this.setState({ registering: true });
    try {
      const payload = {
        email: values.email.trim().toLowerCase(),
        password: values.password,
        name: values.name,
        username: values.username,
        company: values.company,
        phone: values.phone,
        country: values.country,
        termsAccepted: values.termsAccepted,
      };
      const responseData = await this.props.auth.registerUser(payload) || {};
      const successMessage = responseData.message || 'Account created. Please check your email for the verification code.';
      const registeredEmail = responseData.email || payload.email;
      message.success(successMessage);
      if (this.loginFormRef.current) {
        this.loginFormRef.current.setFieldsValue({
          email: registeredEmail,
          password: payload.password,
        });
        this.updateLoginButtonState();
      }
      this.setState({
        activeTab: 'login',
        loginNeedsOtp: true,
        pendingVerificationEmail: registeredEmail,
        loginStatusMessage: 'Account created. Please check your email for the 6 digit verification code, then enter it below.',
      });
      this.props.auth.openAuthView('login');
    } catch (error) {
      const errorMessage = getErrorMessage(error, 'Failed to register user.');
      message.error(errorMessage);
    } finally {
      this.setState({ registering: false });
    }
  };

  handleResendVerification = async () => {
    const formEmail = this.loginFormRef.current
      ? this.loginFormRef.current.getFieldValue('email')
      : '';
    const normalizedEmail = (formEmail || this.state.pendingVerificationEmail || '').trim().toLowerCase();
    if (!normalizedEmail) {
      message.error('Enter your email address first.');
      return;
    }

    this.setState({ resendingVerification: true });
    try {
      const responseData = await this.props.auth.resendUserRegistrationVerification({
        email: normalizedEmail,
      });
      message.success((responseData && responseData.message) || 'If verification is still required, we sent another verification email.');
      this.setState({
        loginNeedsOtp: true,
        pendingVerificationEmail: normalizedEmail,
        loginStatusMessage: 'Please check your email for the 6 digit verification code, then enter it below.',
      });
    } catch (error) {
      const errorMessage = getErrorMessage(error, 'Unable to resend verification email.');
      message.error(errorMessage);
    } finally {
      this.setState({ resendingVerification: false });
    }
  };

  renderLoginForm() {
    const {
      loggingIn,
      loginNeedsOtp,
      loginStatusMessage,
      pendingVerificationEmail,
      disableLoginSubmit,
      resendingVerification,
    } = this.state;
    return (
      <>
        {loginStatusMessage && (
          <Alert
            type="info"
            message={loginStatusMessage}
            style={{ marginBottom: 16 }}
            showIcon
          />
        )}
        <Form
          layout="vertical"
          ref={this.loginFormRef}
          onFinish={this.handleLogin}
          requiredMark={false}
          onFieldsChange={this.handleLoginFieldsChange}
          className="auth-form"
          initialValues={{
            email: pendingVerificationEmail || undefined,
          }}
        >
          <Form.Item
            name="email"
            label="Email"
            rules={[
              { required: true, message: 'Email is required' },
              { type: 'email', message: 'Enter a valid email' },
            ]}
          >
            <Input placeholder="you@example.com" size="large" />
          </Form.Item>
          <Form.Item
            name="password"
            label="Password"
            rules={[
              { required: true, message: 'Password is required' },
            ]}
          >
            <Input.Password placeholder="Password" size="large" />
          </Form.Item>
          {loginNeedsOtp && (
            <>
              <Form.Item
                name="otp"
                label="One-time password"
                rules={[
                  { required: true, message: 'Enter the 6 digit code from your email' },
                  { len: 6, message: 'OTP must be 6 digits' },
                ]}
              >
                <Input placeholder="6 digit code" maxLength={6} size="large" />
              </Form.Item>
              <div className="auth-helper-text auth-helper-text--resend">
                <span>Didn&apos;t receive the email?</span>
                <Button
                  type="link"
                  className="auth-resend-button"
                  onClick={this.handleResendVerification}
                  loading={resendingVerification}
                  disabled={resendingVerification}
                >
                  Resend Email
                </Button>
              </div>
            </>
          )}
          <Form.Item>
            <Button
              type="primary"
              htmlType="submit"
              block
              size="large"
              loading={loggingIn}
              disabled={disableLoginSubmit}
            >
              {loginNeedsOtp ? 'Verify & sign in' : 'Log in'}
            </Button>
          </Form.Item>
          <div className="auth-helper-text auth-helper-text--actions">
            <button
              type="button"
              className="auth-link-button"
              onClick={this.openPasswordReset}
            >
              Forgot your password? Send me an OTP.
            </button>
          </div>
        </Form>
      </>
    );
  }

  renderRegisterForm() {
    const { registering, disableRegisterSubmit } = this.state;
    return (
      <Form
        layout="vertical"
        ref={this.registerFormRef}
        onFinish={this.handleRegister}
        requiredMark={false}
        onFieldsChange={this.handleRegisterFieldsChange}
        className="auth-form"
      >
        <Form.Item
          name="email"
          label="Work email"
          rules={[
            { required: true, message: 'Email is required' },
            { type: 'email', message: 'Enter a valid email' },
          ]}
        >
          <Input placeholder="you@example.com" size="large" />
        </Form.Item>
        <Form.Item
          name="username"
          label="Username"
          rules={[
            { required: true, message: 'Username is required' },
            { min: 2, message: 'Username should be at least 2 characters' },
          ]}
        >
          <Input placeholder="Preferred username" size="large" />
        </Form.Item>
        <Form.Item
          name="name"
          label="Full name"
          rules={[
            { max: 120, message: 'Name is too long' },
          ]}
        >
          <Input placeholder="Full name (optional)" size="large" />
        </Form.Item>
        <Form.Item
          name="company"
          label="Company"
          rules={[
            { required: true, message: 'Company is required' },
            { min: 2, message: 'Company must be at least 2 characters' },
          ]}
        >
          <Input placeholder="Company name" size="large" />
        </Form.Item>
        <Form.Item
          name="phone"
          label="Contact phone"
          rules={[
            { required: true, message: 'Contact phone is required' },
            { min: 6, message: 'Enter a valid phone number' },
          ]}
        >
          <Input placeholder="+1 555 123 4567" size="large" />
        </Form.Item>
        <Form.Item
          name="country"
          label="Country"
          rules={[
            { required: true, message: 'Country is required' },
          ]}
        >
          <Select
            showSearch
            size="large"
            placeholder="Select country"
            optionFilterProp="children"
          >
            {COUNTRIES.map((country) => (
              <Select.Option key={country} value={country}>
                {country}
              </Select.Option>
            ))}
          </Select>
        </Form.Item>
        <Form.Item
          name="password"
          label="Password"
          rules={[
            { required: true, message: 'Password is required' },
            { min: 8, message: 'Password must be at least 8 characters' },
            {
              pattern: /[a-z]/,
              message: 'Include at least one lowercase letter',
            },
            {
              pattern: /[0-9]/,
              message: 'Include at least one number',
            },
          ]}
          hasFeedback
        >
          <Input.Password placeholder="Secure password" size="large" />
        </Form.Item>
        <Form.Item
          name="confirmPassword"
          label="Confirm password"
          dependencies={['password']}
          hasFeedback
          rules={[
            { required: true, message: 'Please confirm your password' },
            ({ getFieldValue }) => ({
              validator(_, value) {
                if (!value || getFieldValue('password') === value) {
                  return Promise.resolve();
                }
                return Promise.reject(new Error('Passwords do not match'));
              },
            }),
          ]}
        >
          <Input.Password placeholder="Re-enter password" size="large" />
        </Form.Item>
        <Form.Item
          name="termsAccepted"
          valuePropName="checked"
          rules={[
            {
              validator(_, value) {
                if (value) {
                  return Promise.resolve();
                }
                return Promise.reject(
                  new Error('You must agree to the Terms of Use before signing up'),
                );
              },
            },
          ]}
        >
          <Checkbox>
            I have read and agree to the{' '}
            <Link
              to="/terms-of-use"
              target="_blank"
              rel="noopener noreferrer"
              className="auth-terms-link"
            >
              Terms of Use
            </Link>
            {' '}and acknowledge the{' '}
            <Link
              to="/privacy-policy"
              target="_blank"
              rel="noopener noreferrer"
              className="auth-terms-link"
            >
              Privacy Policy
            </Link>
            .
          </Checkbox>
        </Form.Item>
        <Form.Item>
          <Button
            type="primary"
            htmlType="submit"
            block
            size="large"
            loading={registering}
            disabled={disableRegisterSubmit}
          >
            Create account
          </Button>
        </Form.Item>
        <div className="auth-helper-text">
          We&apos;ll send a verification code to this email so you can activate your account.
        </div>
      </Form>
    );
  }

  renderResetForm() {
    const {
      requestingResetOtp,
      resettingPassword,
      resetStep,
      resetStatusMessage,
      resetEmail,
      disableResetSubmit,
    } = this.state;

    const submitText = resetStep === 'confirm' ? 'Reset password' : 'Send OTP';

    return (
      <>
        {resetStatusMessage && (
          <Alert
            type="info"
            message={resetStatusMessage}
            style={{ marginBottom: 16 }}
            showIcon
          />
        )}
        <Form
          layout="vertical"
          ref={this.resetFormRef}
          onFinish={this.handleResetSubmit}
          requiredMark={false}
          onFieldsChange={this.handleResetFieldsChange}
          className="auth-form"
          initialValues={{
            email: resetEmail || undefined,
          }}
        >
          <Form.Item
            name="email"
            label="Email"
            rules={[
              { required: true, message: 'Email is required' },
              { type: 'email', message: 'Enter a valid email' },
            ]}
          >
            <Input placeholder="you@example.com" size="large" />
          </Form.Item>
          {resetStep === 'confirm' && (
            <>
              <Form.Item
                name="otp"
                label="One-time password"
                rules={[
                  { required: true, message: 'Enter the 6 digit code from your email' },
                  { len: 6, message: 'OTP must be 6 digits' },
                ]}
              >
                <Input placeholder="6 digit code" maxLength={6} size="large" />
              </Form.Item>
              <Form.Item
                name="password"
                label="New password"
                rules={[
                  { required: true, message: 'Password is required' },
                  { min: 8, message: 'Password must be at least 8 characters' },
                  {
                    pattern: /[a-z]/,
                    message: 'Include at least one lowercase letter',
                  },
                  {
                    pattern: /[0-9]/,
                    message: 'Include at least one number',
                  },
                ]}
                hasFeedback
              >
                <Input.Password placeholder="New password" size="large" />
              </Form.Item>
              <Form.Item
                name="confirmPassword"
                label="Confirm new password"
                dependencies={['password']}
                hasFeedback
                rules={[
                  { required: true, message: 'Please confirm your new password' },
                  ({ getFieldValue }) => ({
                    validator(_, value) {
                      if (!value || getFieldValue('password') === value) {
                        return Promise.resolve();
                      }
                      return Promise.reject(new Error('Passwords do not match'));
                    },
                  }),
                ]}
              >
                <Input.Password placeholder="Re-enter new password" size="large" />
              </Form.Item>
            </>
          )}
          <Form.Item>
            <Button
              type="primary"
              htmlType="submit"
              block
              size="large"
              loading={requestingResetOtp || resettingPassword}
              disabled={disableResetSubmit}
            >
              {submitText}
            </Button>
          </Form.Item>
          {resetStep === 'confirm' && (
            <div className="auth-helper-text auth-helper-text--actions">
              <button
                type="button"
                className="auth-link-button"
                onClick={this.handleResetBack}
              >
                Resend OTP or change email
              </button>
            </div>
          )}
        </Form>
      </>
    );
  }

  render() {
    const { auth } = this.props;
    const { activeTab } = this.state;
    const tabs = [
      {
        label: 'Log in',
        key: 'login',
        children: this.renderLoginForm(),
      },
      {
        label: 'Sign up',
        key: 'register',
        children: this.renderRegisterForm(),
      },
      {
        label: 'Reset password',
        key: 'reset',
        children: this.renderResetForm(),
      },
    ];

    return (
      <div className={`auth-page-overlay ${auth.authViewVisible ? 'auth-page-overlay--visible' : ''}`}>
        <div className="auth-card">
          <div className="auth-hero">
            <div className="auth-hero__logo">
              SO<span className="auth-hero__logo-accent">A</span>L
              <span className="auth-hero__logo-accent">I</span>VE
            </div>
            <div className="auth-hero__content">
              <h2>Bring Your Ideas to Life</h2>
              <p>
                Login or create an account to access the AI image generator, manage
                your prompts, and collaborate with teammates.
              </p>
            </div>
          </div>
          <div className="auth-panel">
            <button
              type="button"
              className="auth-close"
              onClick={auth.closeAuthView}
              aria-label="Close authentication"
            >
              ×
            </button>
            <h1 className="auth-panel__title">Login or Signup</h1>
            <Tabs
              activeKey={activeTab}
              onChange={this.setActiveTab}
              items={tabs}
              className="auth-tabs"
            />
          </div>
        </div>
      </div>
    );
  }
}

const mapStateToProps = (state) => ({
  authState: {
    user: state.auth.user,
    token: state.auth.token,
    authViewVisible: state.auth.authViewVisible,
    authViewMode: state.auth.authViewMode,
  },
});

const mapDispatchToProps = {
  openAuthView,
  closeAuthView,
  setSession,
  clearSession,
  loginUser,
  registerUser,
  verifyUserRegistration,
  resendUserRegistrationVerification,
  requestPasswordReset,
  resetPassword,
};

const mergeProps = (stateProps, dispatchProps, ownProps) => ({
  ...ownProps,
  auth: {
    ...stateProps.authState,
    openAuthView: dispatchProps.openAuthView,
    closeAuthView: dispatchProps.closeAuthView,
    setSession: dispatchProps.setSession,
    clearSession: dispatchProps.clearSession,
    loginUser: dispatchProps.loginUser,
    registerUser: dispatchProps.registerUser,
    verifyUserRegistration: dispatchProps.verifyUserRegistration,
    resendUserRegistrationVerification: dispatchProps.resendUserRegistrationVerification,
    requestPasswordReset: dispatchProps.requestPasswordReset,
    resetPassword: dispatchProps.resetPassword,
  },
});

export default connect(mapStateToProps, mapDispatchToProps, mergeProps)(AuthPage);
